/**
 * Os grupos de uma campanha: quais estão dentro, quais podem receber gente, e o que ainda
 * dá para vincular.
 *
 * Duas decisões de tela que valem o comentário:
 *
 *  - **"Vinculados" e "podem receber agora" são números diferentes, e os dois aparecem.**
 *    Uma campanha com 3 grupos em que 2 estão cheios distribui para 1. Mostrar só o total
 *    daria o número certo pela conta errada — e é assim que se descobre o problema pela
 *    queda no resultado, dias depois.
 *  - **Grupo arquivado continua listado**, marcado como tal. Some da distribuição, não da
 *    tela: uma campanha que perdeu grupos precisa mostrar que os perdeu.
 *
 *  - **Vincular é em lote, com busca.** Quem monta campanha monta BLOCO: "os 20 grupos
 *    Achadinhos #140–#159". Um seletor de um grupo por vez obrigava 20 idas e voltas — e a
 *    ordem da fila saía na ordem dos cliques (#140, #148, #141…). Agora a busca filtra pelo
 *    nome, "marcar os encontrados" pega o bloco, e os marcados entram na ordem do nome.
 *
 * Gaveta e não página: vincular grupo é ação de rotina feita a partir da lista, e a gaveta
 * `responsive` do design system já vira painel de baixo no celular (§11).
 */
import { useEffect, useMemo } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorState,
  Input,
  Spinner,
} from '@mgdlead/ui';
import { Link2, Search, Unlink, Users } from 'lucide-react';
import { matchesSearch } from '../../../features/group/group-search';
import { compareNatural } from '../../../lib/sequential';
import {
  campaignGroupName,
  countEligible,
  groupEligibility,
} from '../../../features/campaign/campaign-ui';
import { groupDisplayName } from '../../../features/group/group-ui';
import { useCampaignGroups, useGroups } from '../../../lib/api';
import type { Campaign } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { SelectAllBar } from '../../../features/monitor/SelectAllBar';
import { useState } from 'react';

export interface CampaignGroupsDrawerProps {
  workspaceId: string;
  campaign: Campaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canUpdate: boolean;
  pending: boolean;
  /** Vincular os marcados, na ordem recebida (já em ordem de nome). */
  onLinkMany: (groupIds: string[]) => Promise<void>;
  /** Vínculo em lote em andamento: quantos já foram, de quantos. */
  progress: { feitos: number; total: number } | null;
  onUnlink: (groupId: string) => void;
}

export function CampaignGroupsDrawer({
  workspaceId,
  campaign,
  open,
  onOpenChange,
  canUpdate,
  pending,
  onLinkMany,
  progress,
  onUnlink,
}: CampaignGroupsDrawerProps) {
  const campaignId = open && campaign ? campaign.id : null;
  const groupsQuery = useCampaignGroups(workspaceId, campaignId);
  // Todos os grupos vivos do workspace — a origem da lista de "vincular".
  const allGroupsQuery = useGroups(workspaceId);

  const linked = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const linkedIds = useMemo(() => new Set(linked.map((g) => g.groupId)), [linked]);

  /** O que dá para vincular: grupo vivo do workspace que ainda não está na campanha. */
  const disponiveis = useMemo(
    () =>
      (allGroupsQuery.data?.groups ?? [])
        .filter((g) => !linkedIds.has(g.id))
        .sort((a, b) => compareNatural(groupDisplayName(a), groupDisplayName(b))),
    [allGroupsQuery.data, linkedIds],
  );

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Outra campanha, outra lista: marcação e busca não atravessam de uma para a outra.
  useEffect(() => {
    setQuery('');
    setSelected(new Set());
  }, [campaignId]);

  const encontrados = useMemo(
    () => disponiveis.filter((g) => matchesSearch(g, query)),
    [disponiveis, query],
  );
  const marcadosVisiveis = encontrados.filter((g) => selected.has(g.id)).length;
  /** Os marcados que ainda estão disponíveis, na ordem da lista (= ordem do nome). */
  const marcados = disponiveis.filter((g) => selected.has(g.id));
  const vinculando = progress !== null;

  function toggle(groupId: string) {
    setSelected((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(groupId)) proximo.delete(groupId);
      else proximo.add(groupId);
      return proximo;
    });
  }

  /** "Selecionar todos" age sobre o que a busca deixou passar. */
  function marcarVisiveis(marcar: boolean) {
    setSelected((atual) => {
      const proximo = new Set(atual);
      for (const g of encontrados) {
        if (marcar) proximo.add(g.id);
        else proximo.delete(g.id);
      }
      return proximo;
    });
  }

  const elegiveis = countEligible(linked);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={campaign ? `Grupos de ${campaign.name}` : 'Grupos da campanha'}
      description={
        campaign
          ? 'Os grupos vinculados recebem as pessoas que clicam no link desta campanha, na ordem da lista.'
          : undefined
      }
    >
      {campaign === null ? null : (
        <div className="flex flex-col gap-4">
          {/* ------------------------- o par de números ------------------------- */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone="neutral" numeric>
              {linked.length} {linked.length === 1 ? 'vinculado' : 'vinculados'}
            </Badge>
            {/*
              O número que decide a operação. `0` aqui é mesmo zero — a campanha existe,
              tem grupos e não tem para onde mandar ninguém —, e por isso vem em tom de
              alerta em vez de sumir.
            */}
            <Badge tone={elegiveis === 0 ? 'warning' : 'success'} dot numeric>
              {elegiveis} {elegiveis === 1 ? 'pode receber' : 'podem receber'}
            </Badge>
          </div>

          {linked.length > 0 && elegiveis === 0 ? (
            <p className="text-sm text-warning">
              Nenhum dos grupos vinculados pode receber gente agora — todos estão cheios, pausados
              ou arquivados. Quem clicar no link desta campanha não vai ter para onde ir.
            </p>
          ) : null}

          {/* ---------------------------- vincular ---------------------------- */}
          {canUpdate ? (
            <div className="flex flex-col gap-2 border-b border-border pb-4">
              {disponiveis.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {allGroupsQuery.data?.groups.length === 0
                    ? 'Você ainda não importou nenhum grupo. Importe os grupos na tela de Grupos antes de montar a campanha.'
                    : 'Todos os seus grupos já estão nesta campanha.'}
                </p>
              ) : (
                <>
                  <Input
                    search
                    size="sm"
                    leadingIcon={<Search />}
                    placeholder="Buscar pelo nome — ex.: achadinhos"
                    aria-label="Buscar grupo para vincular"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-1">
                      <Badge tone={marcados.length > 0 ? 'success' : 'neutral'} numeric dot>
                        {marcados.length} {marcados.length === 1 ? 'marcado' : 'marcados'}
                      </Badge>
                      <Badge tone="neutral" numeric>
                        {encontrados.length} de {disponiveis.length} na lista
                      </Badge>
                    </span>
                  </div>

                  <SelectAllBar
                    visible={encontrados.length}
                    selectedVisible={marcadosVisiveis}
                    selectedTotal={marcados.length}
                    filtered={query.trim() !== ''}
                    disabled={vinculando}
                    onSelectVisible={() => marcarVisiveis(true)}
                    onUnselectVisible={() => marcarVisiveis(false)}
                    onClear={() => setSelected(new Set())}
                  />

                  {encontrados.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhum grupo com esse nome. Tente uma palavra só.
                    </p>
                  ) : (
                    <ul className="max-h-[40dvh] overflow-y-auto divide-y divide-border rounded-lg border border-border">
                      {encontrados.map((group) => (
                        <li key={group.id} className="px-3 py-2">
                          <Checkbox
                            checked={selected.has(group.id)}
                            disabled={vinculando}
                            onChange={() => toggle(group.id)}
                            label={groupDisplayName(group)}
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Ação importante = largura cheia no celular (DESIGN_SYSTEM §11). */}
                  <Button
                    variant="primary"
                    leadingIcon={<Link2 />}
                    block
                    // Sem `loading`: ele esconde o texto, e o texto É o progresso.
                    disabled={marcados.length === 0 || pending || vinculando}
                    onClick={() => {
                      void onLinkMany(marcados.map((g) => g.id)).then(() => setSelected(new Set()));
                    }}
                  >
                    {progress !== null
                      ? `Vinculando ${progress.feitos} de ${progress.total}…`
                      : marcados.length === 0
                        ? 'Marque os grupos para vincular'
                        : marcados.length === 1
                          ? 'Vincular 1 grupo'
                          : `Vincular ${marcados.length} grupos`}
                  </Button>
                </>
              )}
            </div>
          ) : null}

          {/* ---------------------------- vinculados ---------------------------- */}
          {groupsQuery.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : groupsQuery.isError ? (
            <ErrorState
              title="Não conseguimos carregar os grupos desta campanha"
              description={humanizeError(groupsQuery.error)}
              onRetry={() => void groupsQuery.refetch()}
            />
          ) : linked.length === 0 ? (
            <EmptyState
              kind="no-data"
              icon={Users}
              compact
              title="Nenhum grupo vinculado"
              description="Uma campanha sem grupo não distribui ninguém. Vincule pelo menos um grupo acima."
            />
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {linked.map((group, index) => {
                const eligibility = groupEligibility(group);
                const nome = campaignGroupName(group);
                return (
                  <li key={group.groupId} className="flex items-start justify-between gap-2 py-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span
                        className={group.name === null ? 'text-muted-foreground' : 'font-medium'}
                      >
                        {nome}
                      </span>
                      <span className="text-2xs text-muted-foreground">
                        {group.monitorName} · {index + 1}º da fila
                      </span>
                      <Badge tone={eligibility.tone} dot>
                        {eligibility.label}
                      </Badge>
                    </div>
                    {canUpdate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Unlink />}
                        aria-label={`Desvincular ${nome}`}
                        disabled={pending}
                        onClick={() => onUnlink(group.groupId)}
                      >
                        Desvincular
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-2xs text-muted-foreground">
            Desvincular tira o grupo desta campanha e não mexe no grupo: as pessoas continuam lá
            dentro e o histórico de entradas e saídas continua guardado.
          </p>
        </div>
      )}
    </Drawer>
  );
}
